import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

export type GoldenButtonOffer = {
  id: number;
  popup_title: string;
  popup_text: string;
  popup_image_url: string;
  cta_text: string;
  cta_url: string;
  outbound_url?: string | null;
};

const FAKE_NAMES = [
  'Al***', 'Kr***', 'Ma***', 'Jo***', 'Da***', 'An***', 'Mi***', 'Sa***',
  'Pa***', 'Ni***', 'Ol***', 'Vi***', 'Se***', 'Iv***', 'De***', 'Ar***',
  'Em***', 'Lu***', 'To***', 'Ka***',
];
const FAKE_AMOUNTS = ['€250', '€500', '€1,000', '€1,250', '€2,500', '€5,000'];

type FakeCloud = { id: number; name: string; amount: string; left: number };

function useSocialProofClouds(active: boolean) {
  const [clouds, setClouds] = useState<FakeCloud[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    if (!active) {
      setClouds([]);
      return;
    }
    const spawn = () => {
      const name = FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)];
      const amount = FAKE_AMOUNTS[Math.floor(Math.random() * FAKE_AMOUNTS.length)];
      const left = 10 + Math.floor(Math.random() * 60); // 10%-70%
      const id = ++idRef.current;
      setClouds(prev => {
        const next = [...prev, { id, name, amount, left }];
        // keep max 2
        return next.length > 2 ? next.slice(-2) : next;
      });
      // Remove after 3.5s
      setTimeout(() => setClouds(prev => prev.filter(c => c.id !== id)), 3500);
    };
    // First one after 1.5s
    const t1 = setTimeout(spawn, 1500);
    // Then every 3-4s
    const interval = setInterval(spawn, 3000 + Math.random() * 1000);
    return () => {
      clearTimeout(t1);
      clearInterval(interval);
    };
  }, [active]);

  return clouds;
}

export function GoldenButtonPopup(props: {
  open: boolean;
  offer: GoldenButtonOffer | null;
  onClose: () => void;
  onCtaClick: () => void;
}) {
  const { open, offer, onClose, onCtaClick } = props;
  const [closing, setClosing] = useState(false);
  const clouds = useSocialProofClouds(open && !!offer);

  const reducedMotion = useMemo(() => {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    } catch {
      return false;
    }
  }, []);

  if (!open || !offer) return null;

  const handleClose = () => {
    if (reducedMotion) {
      onClose();
      return;
    }
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 200);
  };

  return (
    <div
      class={`golden-popup-backdrop ${closing ? 'closing' : ''}`}
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: closing ? 'fadeOut 150ms ease-out forwards' : 'fadeIn 150ms ease-out',
      }}
    >
      <div
        class={`golden-popup ${closing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: '360px',
          background: '#1a0a0a',
          border: '2px solid rgba(255, 215, 0, 0.5)',
          boxShadow: '0 0 30px rgba(255, 215, 0, 0.2)',
          borderRadius: '24px',
          padding: '24px',
          position: 'relative',
          animation: closing
            ? 'scaleOut 200ms ease-out forwards'
            : 'scaleIn 200ms ease-out',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.1)',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 150ms, background 150ms',
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Offer image */}
        {offer.popup_image_url && (
          <div
            style={{
              width: '100%',
              height: '150px',
              borderRadius: '12px',
              overflow: 'hidden',
              marginBottom: '16px',
            }}
          >
            <img
              src={offer.popup_image_url}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        {/* Title */}
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#FFD700',
            margin: '0 0 12px 0',
            textAlign: 'center',
          }}
        >
          {offer.popup_title || '🎰 Free Spins Bonus!'}
        </h2>

        {/* Description */}
        {offer.popup_text && (
          <p
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.7)',
              margin: '0 0 20px 0',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {offer.popup_text}
          </p>
        )}

        {/* CTA button */}
        <button
          onClick={onCtaClick}
          style={{
            width: '100%',
            height: '52px',
            borderRadius: '26px',
            border: 'none',
            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
            color: '#000',
            fontSize: '16px',
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
            transition: 'transform 100ms, box-shadow 100ms',
          }}
        >
          {offer.cta_text || 'Get Free Spins'}
        </button>
      </div>

      {/* Social proof floating clouds */}
      {clouds.map(c => (
        <div
          key={c.id}
          style={{
            position: 'absolute',
            left: `${c.left}%`,
            bottom: '20%',
            background: 'rgba(255,215,0,0.15)',
            border: '1px solid rgba(255,215,0,0.3)',
            borderRadius: '20px',
            padding: '6px 14px',
            fontSize: '11px',
            fontWeight: 600,
            color: 'rgba(255,255,255,0.85)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            animation: 'cloudFloat 3.5s ease-out forwards',
            zIndex: 10002,
          }}
        >
          {c.name} won {c.amount}!
        </div>
      ))}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes scaleOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.9);
          }
        }

        .golden-popup button:hover {
          opacity: 0.9;
        }

        .golden-popup button:active {
          transform: scale(0.98);
        }

        @keyframes cloudFloat {
          0% {
            opacity: 0;
            transform: translateY(0);
          }
          10% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(-60px);
          }
        }
      `}</style>
    </div>
  );
}
